import React, { useMemo, useState } from 'react';
import { useAdminWarmers } from '../../hooks/useAdminWarmers.js';
import { useLucideIcons } from '../../hooks/usePageAssets.js';

const WarmersSection = ({ onNotify }) => {
  const { items, loading, error, refresh, saveWarmer, deleteWarmer } = useAdminWarmers();
  const [search, setSearch] = useState('');
  const [editorState, setEditorState] = useState({ open: false, record: null, saving: false });

  useLucideIcons([items.length, search, editorState.open]);

  const filteredItems = useMemo(() => {
    const query = search.toLowerCase();
    if (!query) {
      return items;
    }
    return items.filter((item) => {
      const name = item.nombre?.toLowerCase() || '';
      const description = item.descripcion?.toLowerCase() || '';
      return name.includes(query) || description.includes(query);
    });
  }, [items, search]);

  const handleOpenModal = (record = null) => {
    setEditorState({
      open: true,
      saving: false,
      record: record
        ? { ...record }
        : { nombre: '', descripcion: '', activo: true }
    });
  };

  const handleSave = async (values) => {
    setEditorState((prev) => ({ ...prev, saving: true }));
    try {
      await saveWarmer(values);
      setEditorState({ open: false, record: null, saving: false });
      refresh();
      onNotify?.({
        type: 'success',
        message: values?.id ? 'Warmer actualizado correctamente.' : 'Warmer creado correctamente.'
      });
    } catch (err) {
      setEditorState((prev) => ({ ...prev, saving: false }));
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo guardar el warmer.' });
    }
  };

  const handleDelete = async (record) => {
    if (!record) {
      return;
    }
    const confirmed = window.confirm(`¿Eliminar el warmer "${record.nombre}"?`);
    if (!confirmed) {
      return;
    }
    try {
      await deleteWarmer(record.id);
      onNotify?.({ type: 'success', message: 'Warmer eliminado.' });
      refresh();
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo eliminar el warmer.' });
    }
  };

  const closeModal = () => {
    setEditorState({ open: false, record: null, saving: false });
  };

  return (
    <section id="warmers-section" className="admin-section active">
      <div className="section-header">
        <h2 className="section-title">Warmers</h2>
        <div className="section-actions">
          <div className="search-container">
            <span className="search-icon"><i data-lucide="search" /></span>
            <input
              type="text"
              className="form-input"
              placeholder="Buscar warmers..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search ? (
              <button type="button" className="btn-secondary" onClick={() => setSearch('')}>
                Limpiar
              </button>
            ) : null}
          </div>
          <button className="btn-primary" type="button" onClick={() => handleOpenModal()}>
            <span className="btn-icon"><i data-lucide="plus" /></span>
            Nuevo Warmer
          </button>
        </div>
      </div>
      <div className="data-table-container">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <div className="loading-text">Cargando warmers...</div>
          </div>
        ) : (
          <table className="data-table" id="warmers-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Descripción</th>
                <th>Restaurantes</th>
                <th>Recetas</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr>
                  <td colSpan={6} className="empty-state">
                    <div className="empty-state-icon"><i data-lucide="alert-triangle" /></div>
                    <div className="empty-state-text">{error}</div>
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty-state">
                    <div className="empty-state-icon"><i data-lucide="flame" /></div>
                    <div className="empty-state-text">No hay warmers configurados.</div>
                    <div className="empty-state-subtext">Crea un warmer para comenzar.</div>
                  </td>
                </tr>
              ) : (
                filteredItems.map((warmer) => (
                  <tr key={warmer.id}>
                    <td>
                      <strong>{warmer.nombre}</strong>
                    </td>
                    <td>{warmer.descripcion || '—'}</td>
                    <td><span className="process-count">{warmer.restaurants_count || 0}</span></td>
                    <td><span className="process-count">{warmer.recipes_count || 0}</span></td>
                    <td>
                      <span className={`status-badge ${warmer.activo ? 'status-active' : 'status-inactive'}`}>
                        {warmer.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="action-btn edit" type="button" onClick={() => handleOpenModal(warmer)}>
                          <i data-lucide="edit" />
                        </button>
                        <button className="action-btn delete" type="button" onClick={() => handleDelete(warmer)}>
                          <i data-lucide="trash-2" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      {editorState.open ? (
        <WarmerModal state={editorState} onClose={closeModal} onSubmit={handleSave} />
      ) : null}
    </section>
  );
};

const WarmerModal = ({ state, onClose, onSubmit }) => {
  const [formValues, setFormValues] = useState(() => ({
    ...state.record,
    nombre: state.record?.nombre || '',
    descripcion: state.record?.descripcion || '',
    activo: state.record?.activo ?? true
  }));

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({ ...formValues, id: state.record?.id || null });
  };

  return (
    <div className="modal" style={{ display: 'block' }}>
      <div className="modal-content" style={{ maxWidth: '520px' }}>
        <div className="modal-header">
          <h3>{state.record?.id ? 'Editar Warmer' : 'Nuevo Warmer'}</h3>
          <button className="modal-close" type="button" onClick={onClose}>
            <i data-lucide="x" />
          </button>
        </div>
        <form className="modal-body form-grid" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Nombre</label>
            <input
              type="text"
              className="form-input"
              name="nombre"
              value={formValues.nombre}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Descripción</label>
            <textarea
              className="form-input"
              name="descripcion"
              value={formValues.descripcion}
              onChange={handleChange}
              rows={3}
              placeholder="Describe el tipo de warmer..."
            />
          </div>
          <div className="form-group checkbox-group">
            <label className="checkbox-item">
              <input
                type="checkbox"
                name="activo"
                checked={formValues.activo}
                onChange={handleChange}
              />
              <span className="checkmark" />
              <span className="checkbox-label">Warmer activo</span>
            </label>
          </div>
          <div className="modal-footer">
            <button className="btn-secondary" type="button" onClick={onClose} disabled={state.saving}>
              Cancelar
            </button>
            <button className="btn-primary" type="submit" disabled={state.saving}>
              {state.saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WarmersSection;
